import { createSelector } from '@reduxjs/toolkit';
import { filter, length, map, prop, values } from 'ramda';

import ooeConstants from '../constants';
import { selectIndexedMenuItems, selectMenuPaperGoods } from '../menu/selectors';
import { cartItemAdapter, CartItemEntity, initialState, modifierAdapter } from './reducer';
import { getModTypeThatAccountsForPickles, mapCartItems } from './utils';

export const selectCart = (state: { cart: typeof initialState }) => state.cart;

const cartItemSelectors = cartItemAdapter.getSelectors(selectCart);

// Declared as a standalone export so Storybook's module automocking can re-export it
export const selectAllCartItems = cartItemSelectors.selectAll;

export const { selectEntities: selectModifierEntities } = modifierAdapter.getSelectors();

export const selectCartItemsLength = createSelector(selectAllCartItems, (cart) =>
  cart.reduce((acc, val) => acc + val.quantity, 0),
);

export const selectPromoFreeActive = createSelector(selectAllCartItems, (cart) =>
  cart.some((c) => c.promoFree === true),
);

export const selectPromoFreeItemCount = createSelector(selectAllCartItems, (cart) => {
  let itemCount = 0;
  cart.forEach((item) => {
    itemCount += item?.promoFree ? item.quantity : 0;
  });
  return itemCount;
});

export const selectShowMaxPromoFreeItemCountWarning = createSelector(selectPromoFreeItemCount, (count) => {
  return count > ooeConstants.MAX_PROMO_FREE_ITEM_QTY;
});

export const selectMaxPromoFreeQtySubmitWarning = createSelector(
  selectShowMaxPromoFreeItemCountWarning,
  (qtyInvalid) => {
    if (qtyInvalid) {
      return {
        type: 'error',
        message: `Total quantity of promo free items must be less than ${ooeConstants.MAX_PROMO_FREE_ITEM_QTY}`,
      };
    }
    return {};
  },
);

export const selectCartWithPrices = createSelector(
  selectIndexedMenuItems,
  selectAllCartItems,
  (flattenedMenu, cartItems) => {
    const cartItemsWithPrices = map(
      (item: CartItemEntity) => {
        let selectedSide;
        let selectedPremiumSide;
        let selectedDessert;
        const { tag, selectedSideTag, selectedDessertTag, selectedPremiumSideTag, promoFree } =
          item as CartItemEntity & {
            tag: string;
          };
        const modifiers = selectModifierEntities(item.modifiers);
        const menuItem = flattenedMenu[tag];
        const { freeSauces, sideItems, premiumSideItems, dessertItems } = menuItem;
        let freeSaucesRemaining = freeSauces ? freeSauces * item.quantity : 0;

        const calculateModPrice = (mod: CartItemModifier) => {
          const menuMod = menuItem.indexedItems[mod.tag];
          if (!menuMod || !Object.prototype.hasOwnProperty.call(menuMod, 'itemPrice')) {
            // eslint-disable-next-line no-console
            console.warn(`${tag} does not have ${mod.tag} as an option`);
            return 0;
          }
          const { itemPrice } = menuMod;

          if (menuMod.modifierType === 'NO_REFUND_RECIPE') {
            // No refund recipe means the item does not receive a reduced price, so we return 0
            return 0;
          }

          if (menuMod.isSauce === true) {
            const paidQuantity = Math.max(mod.quantity - freeSaucesRemaining, 0);
            freeSaucesRemaining += -(mod.quantity - paidQuantity);
            return itemPrice * paidQuantity;
          }
          return itemPrice * mod.quantity * item.quantity;
        };

        if (selectedSideTag && sideItems && sideItems.length) {
          const foundSide = sideItems.find((sideItem) => sideItem.tag === selectedSideTag) ?? sideItems[0];
          selectedSide = {
            ...foundSide,
            price: item.quantity * foundSide.itemPrice,
          };
        }

        if (selectedPremiumSideTag && premiumSideItems && premiumSideItems.length) {
          const foundPremiumSide =
            premiumSideItems.find((premiumSideItem) => premiumSideItem.tag === selectedPremiumSideTag) ??
            premiumSideItems[0];
          selectedPremiumSide = {
            ...foundPremiumSide,
            price: item.quantity * foundPremiumSide.itemPrice,
          };
        }

        if (selectedDessertTag && dessertItems && dessertItems.length) {
          const foundDessert =
            dessertItems.find((dessertItem) => dessertItem.tag === selectedDessertTag) ?? dessertItems[0];
          selectedDessert = {
            ...foundDessert,
            price: item.quantity * foundDessert.itemPrice,
          };
        }

        const mappedModifiers = map(
          (mod) => ({
            ...menuItem.indexedItems[mod.tag],
            ...mod,
            price: calculateModPrice(mod),
            maximum: menuItem.items
              ?.flatMap((e) => e.items)
              .find((subItem) => subItem?.tag === mod.tag && subItem.maximum)?.maximum,
            modifierType: getModTypeThatAccountsForPickles({
              itemTag: item.tag,
              modifierTag: mod.tag,
              modifierType: menuItem.indexedItems[mod.tag]?.modifierType,
            }),
          }),
          modifiers ? values(modifiers) : ([] as CartItemModifier[]),
        );

        return {
          ...item,
          name: menuItem.name,
          selectedSide,
          selectedDessert,
          selectedPremiumSide,
          modifiers: mappedModifiers,
          items: map(
            (i) => ({
              ...i,
              quantity: prop('quantity', modifiers ? modifiers[i.tag] : { tag: '', quantity: 0 }),
              modifierType: getModTypeThatAccountsForPickles({
                itemTag: item.tag,
                modifierTag: i.tag,
                modifierType: i.modifierType,
              }),
            }),
            values(flattenedMenu[tag].indexedItems),
          ),
          price:
            promoFree && !selectedSide && !selectedDessert && !mappedModifiers.length
              ? ooeConstants.PROMO_FREE_ITEM_PRICE
              : menuItem.itemPrice * item.quantity * Number(!promoFree) +
                mappedModifiers.reduce((acc, mod) => acc + mod.price, 0) +
                (selectedSide?.price ?? 0) +
                (selectedDessert?.price ?? 0) +
                (selectedPremiumSide?.price ?? 0),
        };
      },
      filter(
        (cartItem) => typeof cartItem.tag === 'string' && typeof flattenedMenu[cartItem.tag] === 'object',
        cartItems,
      ),
    );
    return cartItemsWithPrices;
  },
);

export const selectCartHasBreakfastItems = createSelector(
  selectCartWithPrices,
  (items) => length(items.filter((item) => item.dayPart === ooeConstants.DAY_PART_BREAKFAST)) > 0,
);

export const selectCartForLineItems = createSelector(
  selectCartWithPrices,
  selectIndexedMenuItems,
  (cartItems, menuItems) => mapCartItems(cartItems, menuItems),
);

export const selectPaperGoodsCartItems = createSelector(
  selectAllCartItems,
  selectMenuPaperGoods,
  (cartItems, paperGoodsCategory) => {
    const paperGoodsTags = new Set((paperGoodsCategory?.items ?? []).map((item) => item.tag));
    return cartItems.filter((item) => paperGoodsTags.has(item.tag));
  },
);

export const selectCartHasPaperGoods = createSelector(selectPaperGoodsCartItems, (paperGoodsCartItems) =>
  paperGoodsCartItems.some((item) => item.quantity > 0),
);
